import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { pickByLanguage } from "@/lib/language";
import qlivarLogo from "@/assets/qlivar-v2.svg";

const footerNav = [
  { id: "#faq", label: { pt: "FAQ", en: "FAQ" } },
  { href: "/blog", label: { pt: "Blog", en: "Blog" } },
  { href: "/contact", label: { pt: "Contato", en: "Contact" } },
];

const MARKETING_LANDING_PATHS = new Set([
  "/",
  "/pricing",
  "/analytics",
  "/mini-store",
  "/custom-domain",
  "/for-creators",
  "/linktree-alternative",
]);

const productLinks = [
  { id: "#features", label: { pt: "Recursos", en: "Features" } },
  { id: "#story", label: { pt: "Como funciona", en: "How it works" } },
  { id: "#pricing", label: { pt: "Pricing", en: "Pricing" } },
  { href: "/pricing", label: { pt: "Comparar planos", en: "Compare plans" } },
];

export function Footer() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const signupParams = new URLSearchParams(location.search);
  signupParams.set("mode", "signup");
  const signupPath = `/auth?${signupParams.toString()}`;
  const authPath = `/auth${location.search}`;

  const handleAnchorNavigation = (hash: string) =>
    navigate(`${MARKETING_LANDING_PATHS.has(location.pathname) ? location.pathname : "/"}${hash}`);

  return (
    <footer className="border-t border-[#eadbcc] bg-[linear-gradient(180deg,#fff7ef_0%,#fffaf5_100%)]">
      <div className="container py-10 sm:py-14">
        <div className="flex flex-col gap-8 border-b border-[#eadbcc] pb-8 lg:flex-row lg:items-center lg:justify-between">
          <Link to="/" className="flex items-center gap-3 text-[#2f241e]">
            <img src={qlivarLogo} alt="Qlivar" className="h-9 w-9" />
            <span className="font-editorial text-[1.6rem] leading-none tracking-[-0.04em]">Qlivar</span>
          </Link>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-3 text-sm font-medium text-[#66584d]">
            {footerNav.map((item) =>
              item.id ? (
                <button
                  key={item.id}
                  onClick={() => handleAnchorNavigation(item.id)}
                  className="transition-colors hover:text-[#201814]"
                >
                  {pickByLanguage(language, item.label)}
                </button>
              ) : (
                <Link key={item.href} to={item.href!} className="transition-colors hover:text-[#201814]">
                  {pickByLanguage(language, item.label)}
                </Link>
              ),
            )}
            {user ? (
              <Button
                variant="outline"
                size="sm"
                className="rounded-full border-[#dcc5b0] bg-white text-[#2f241e] hover:bg-white"
                onClick={() => navigate("/dashboard")}
              >
                {pickByLanguage(language, { pt: "Painel", en: "Dashboard" })}
              </Button>
            ) : (
              <>
                <button
                  onClick={() => navigate(authPath)}
                  className="text-sm font-medium text-[#66584d] transition-colors hover:text-[#201814]"
                >
                  {pickByLanguage(language, { pt: "Entrar", en: "Log in" })}
                </button>
                <Button
                  variant="hero"
                  size="sm"
                  className="rounded-full shadow-[0_16px_35px_rgba(240,127,69,0.22)] transition-transform duration-150 ease-out hover:brightness-95 active:scale-[0.97]"
                  onClick={() => navigate(signupPath)}
                >
                  {pickByLanguage(language, { pt: "Começar grátis", en: "Start free" })}
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid gap-10 pt-8 lg:grid-cols-[0.7fr_0.5fr_0.8fr]">
          <div>
            <p className="max-w-sm text-sm leading-7 text-[#66584d]">
              {pickByLanguage(language, {
                pt: "A página link-in-bio para creators, consultores e pequenos negócios que querem transformar atenção em receita.",
                en: "The link-in-bio page for creators, consultants, and small businesses turning attention into revenue.",
              })}
            </p>
          </div>

          <div>
            <p className="text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-[#9a7258]">
              {pickByLanguage(language, { pt: "Produto", en: "Product" })}
            </p>
            <div className="mt-4 space-y-3 text-sm text-[#4d4038]">
              {productLinks.map((link) =>
                link.id ? (
                  <button
                    key={link.id}
                    onClick={() => handleAnchorNavigation(link.id)}
                    className="block transition-colors hover:text-[#201814]"
                  >
                    {pickByLanguage(language, link.label)}
                  </button>
                ) : (
                  <Link key={link.href} to={link.href!} className="block transition-colors hover:text-[#201814]">
                    {pickByLanguage(language, link.label)}
                  </Link>
                ),
              )}
            </div>
          </div>

          <div>
            <p className="font-editorial text-[2.4rem] leading-[0.92] tracking-[-0.05em] text-[#201814]">
              {pickByLanguage(language, {
                pt: "Sua audiência já existe.",
                en: "Your audience already exists.",
              })}
            </p>
            <p className="mt-4 max-w-md text-sm leading-7 text-[#66584d]">
              {pickByLanguage(language, {
                pt: "A home nova precisa só fazer uma coisa bem: transformar essa atenção em próxima ação, conversa e venda.",
                en: "The new homepage only needs to do one thing well: turn that attention into the next action, conversation, and sale.",
              })}
            </p>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-3 border-t border-[#eadbcc] pt-6 text-xs text-[#8a7b70] sm:flex-row sm:items-center sm:justify-between">
          <p>
            {pickByLanguage(language, {
              pt: `© ${new Date().getFullYear()} Qlivar. Todos os direitos reservados.`,
              en: `© ${new Date().getFullYear()} Qlivar. All rights reserved.`,
            })}
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            <Link to="/privacy" className="transition-colors hover:text-[#201814]">
              {pickByLanguage(language, { pt: "Privacidade", en: "Privacy" })}
            </Link>
            <Link to="/terms" className="transition-colors hover:text-[#201814]">
              {pickByLanguage(language, { pt: "Termos", en: "Terms" })}
            </Link>
            <Link to="/cookies" className="transition-colors hover:text-[#201814]">
              Cookies
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
